"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { signIn, getSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { PERMISSION_SECTIONS, hasPermission, getPermissionScope, type RolePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wifi } from "lucide-react";
import { isDemoMode } from "@/lib/demo-mode";
import { check2FARequired } from "@/lib/actions/admin-auth";
export default function LoginPage() {
  const $ = _c(40);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("credentials");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  let handleSubmit;
  if ($[0] !== code || $[1] !== email || $[2] !== password || $[3] !== router || $[4] !== step) {
    handleSubmit = async e => {
      e.preventDefault();
      setIsLoading(true);
      setError("");
      if (step === "credentials") {
        const pre = await check2FARequired(email, password);
        if (!pre.success) {
          setError(pre.error || "Email o password non validi");
          setIsLoading(false);
          return;
        }
        if (pre.required) {
          setStep("2fa");
          setIsLoading(false);
          return;
        }
        await finalSignIn();
        return;
      }
      await finalSignIn();
    };
    const finalSignIn = async () => {
      const result = await signIn("credentials", {
        email,
        password,
        code: code || "",
        redirect: false
      });
      if (result?.error) {
        if (result.error === "CredentialsSignin" || /2FA_INVALID/i.test(result.error)) {
          setError("Codice 2FA non valido o credenziali errate");
        } else {
          if (/2FA_REQUIRED/i.test(result.error)) {
            setStep("2fa");
            setError("Inserisci il codice dell'app di autenticazione");
          } else {
            setError("Errore di autenticazione");
          }
        }
        setIsLoading(false);
        return;
      }
      const session = await getSession();
      const user = session?.user as {
        role?: string;
        permissions?: RolePermissions | null;
        hasResellerAccess?: boolean;
        hasTechnicianAccess?: boolean;
        lastActiveMode?: string | null;
      } | undefined;
      const dest = computePostLoginDestination(user);
      router.push(dest);
      router.refresh();
    };
    function computePostLoginDestination(user_0) {
      if (!user_0) {
        return "/";
      }
      if (user_0.role === "ADMIN") {
        return "/";
      }
      const perms = user_0.permissions ?? null;
      if (perms?._all) {
        return "/";
      }
      const hasReseller = !!user_0.hasResellerAccess;
      const hasTech = !!user_0.hasTechnicianAccess;
      if (hasReseller && hasTech) {
        return "/seleziona-area";
      }
      if (hasReseller) {
        return "/reseller";
      }
      if (hasTech) {
        return "/tecnico";
      }
      if (getPermissionScope(perms, "calendario") === "OWN" && hasPermission(perms, "calendario", "view")) {
        return "/tecnico";
      }
      if (hasPermission(perms, "dashboard", "view")) {
        return "/";
      }
      for (const group of PERMISSION_SECTIONS) {
        for (const page of group.pages) {
          if (hasPermission(perms, page.key, "view")) {
            return page.href;
          }
        }
      }
      return "/";
    }
    $[0] = code;
    $[1] = email;
    $[2] = password;
    $[3] = router;
    $[4] = step;
    $[5] = handleSubmit;
  } else {
    handleSubmit = $[5];
  }
  let t0;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col items-center mb-8 gap-1"><span className="text-3xl font-bold tracking-tight">Gest-RT</span><span className="text-xs text-muted-foreground">Gestionale registratori telematici</span></div>;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== error) {
    t1 = error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400 text-center">{error}</div>;
    $[7] = error;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Label htmlFor="email" className="text-sm font-medium">Email</Label>;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => setEmail(e_0.target.value);
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== email) {
    t4 = <div className="space-y-2">{t2}<Input id="email" type="email" placeholder="nome@azienda.it" value={email} onChange={t3} className="h-11 bg-muted/50 border-border/50 focus:border-primary" required={true} /></div>;
    $[11] = email;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Label htmlFor="password" className="text-sm font-medium">Password</Label>;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_1 => setPassword(e_1.target.value);
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = step === "2fa";
  let t8;
  if ($[15] !== password || $[16] !== t7) {
    t8 = <div className="space-y-2">{t5}<Input id="password" type="password" placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} value={password} onChange={t6} className="h-11 bg-muted/50 border-border/50 focus:border-primary" required={true} disabled={t7} /></div>;
    $[15] = password;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== code || $[19] !== step) {
    t9 = step === "2fa" && <div className="space-y-2"><Label htmlFor="code" className="text-sm font-medium">Codice 2FA</Label><Input id="code" type="text" inputMode="text" autoComplete="one-time-code" placeholder="123456 o codice di recupero" value={code} onChange={e_2 => setCode(e_2.target.value)} className="h-11 bg-muted/50 border-border/50 focus:border-primary font-mono tracking-widest text-center" required={true} autoFocus={true} /><p className="text-[11px] text-muted-foreground text-center">Inserisci il codice dell'app oppure un codice di recupero (formato XXXX-XXXX)</p></div>;
    $[18] = code;
    $[19] = step;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isLoading || $[22] !== step) {
    t10 = isLoading ? <span className="flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Accesso in corso...</span> : step === "2fa" ? "Verifica e accedi" : "Accedi";
    $[21] = isLoading;
    $[22] = step;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== isLoading || $[25] !== t10) {
    t11 = <Button type="submit" className="w-full h-11 font-semibold text-sm" disabled={isLoading}>{t10}</Button>;
    $[24] = isLoading;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== step) {
    t12 = step === "2fa" && <button type="button" className="text-xs text-muted-foreground hover:text-foreground w-full text-center" onClick={() => {
      setStep("credentials");
      setCode("");
      setError("");
    }}>← Cambia account</button>;
    $[27] = step;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== handleSubmit || $[30] !== t1 || $[31] !== t11 || $[32] !== t12 || $[33] !== t4 || $[34] !== t8 || $[35] !== t9) {
    t13 = <form onSubmit={handleSubmit} className="space-y-5">{t1}{t4}{t8}{t9}{t11}{t12}</form>;
    $[29] = handleSubmit;
    $[30] = t1;
    $[31] = t11;
    $[32] = t12;
    $[33] = t4;
    $[34] = t8;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="text-center text-xs text-muted-foreground mt-6">© 2026 Gest-RT — Tutti i diritti riservati</p>;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13) {
    t15 = <div className="min-h-screen flex items-center justify-center relative overflow-hidden"><div className="relative z-10 w-full max-w-md mx-4"><div className="glass glass-border rounded-2xl p-8 shadow-2xl shadow-primary/5">{t0}{t13}{t14}</div></div></div>;
    $[38] = t13;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}